import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, shareReplay} from 'rxjs';
import {
  AdjustmentTypeOption,
  BookingOption,
  CurrencyOption,
  InvoiceOption,
  LegalEntityOption,
  SimulateErpResponseRequest,
  SimulateErpResponseResult,
} from '../model/adx-invoice-erp-adjustment-simulation';

/** Backend feature flag (Azure App Configuration) that gates the ERP response simulation. */
export const SIMULATE_ERP_RESPONSE_FEATURE_FLAG = 'ENDPOINT.AdxInvoiceErpAdjustments.SimulateErpResponse.POST';

@Injectable({
  providedIn: 'root'
})
export class AdxInvoiceErpAdjustmentSimulationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'dbov2/adx-invoice-erp-adjustments';

  private adjustmentTypes$?: Observable<AdjustmentTypeOption[]>;
  private legalEntities$?: Observable<LegalEntityOption[]>;
  private currencies$?: Observable<CurrencyOption[]>;

  public getAdjustmentTypes(): Observable<AdjustmentTypeOption[]> {
    this.adjustmentTypes$ ??= this.http.get<AdjustmentTypeOption[]>(`${this.baseUrl}/types`)
      .pipe(catchError(() => of<AdjustmentTypeOption[]>([])), shareReplay(1));
    return this.adjustmentTypes$;
  }

  public getLegalEntities(): Observable<LegalEntityOption[]> {
    this.legalEntities$ ??= this.http.get<LegalEntityOption[]>('dbov2/legal-entity/many')
      .pipe(
        map(items => [...(items ?? [])].sort((a, b) => (a.dataAreaId ?? '').localeCompare(b.dataAreaId ?? ''))),
        catchError(() => of<LegalEntityOption[]>([])),
        shareReplay(1),
      );
    return this.legalEntities$;
  }

  public getCurrencies(): Observable<CurrencyOption[]> {
    this.currencies$ ??= this.http.get<{items: CurrencyOption[]}>('dbov2/currency', {params: {all: true}})
      .pipe(
        map(response => [...(response?.items ?? [])].sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))),
        catchError(() => of<CurrencyOption[]>([])),
        shareReplay(1),
      );
    return this.currencies$;
  }

  public getInvoicesForTrip(tripId: number): Observable<InvoiceOption[]> {
    return this.http.get<{items: InvoiceOption[]}>('dbov2/adx-invoice', {params: {tripId, pageSize: 100}})
      .pipe(map(response => response?.items ?? []));
  }

  public getBookingsForInvoice(adxInvoiceId: number): Observable<BookingOption[]> {
    return this.http.get<{items: BookingOption[]}>('dbov2/adx-booking', {params: {adxInvoiceId, pageSize: 100}})
      .pipe(map(response => response?.items ?? []));
  }

  public simulate(request: SimulateErpResponseRequest): Observable<SimulateErpResponseResult> {
    return this.http.post<SimulateErpResponseResult>(`${this.baseUrl}/simulate`, request);
  }
}
